import React, { useEffect, useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import { subscribeToPush, getExistingSubscription } from '@/config/firebase';
import { Bell, MessageSquare, X, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Storage keys
const PUSH_DISMISSED_KEY = 'rs5_push_dismissed';
const SHORTCUT_DISMISSED_KEY = 'rs5_shortcut_dismissed';
const PUSH_ENABLED_KEY = 'rs5_push_enabled';
const SHORTCUT_INSTALLED_KEY = 'rs5_shortcut_installed';

export default function AppPrompts() {
  const { user } = useAuth();
  const [showPushPrompt, setShowPushPrompt] = useState(false);
  const [showShortcutPrompt, setShowShortcutPrompt] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [loading, setLoading] = useState(false);

  // Check if we should show prompts
  useEffect(() => {
    if (!user) return;

    const checkPrompts = async () => {
      // === PUSH NOTIFICATIONS ===
      // Check if push is already enabled
      const pushEnabled = localStorage.getItem(PUSH_ENABLED_KEY) === 'true';
      const pushDismissedAt = parseInt(localStorage.getItem(PUSH_DISMISSED_KEY) || '0', 10);
      const dismissedRecently = pushDismissedAt && (Date.now() - pushDismissedAt < 3 * 24 * 60 * 60 * 1000);
      
      if (!pushEnabled && !dismissedRecently) {
        // Check browser support
        const hasPushSupport = 'Notification' in window && 
                              'serviceWorker' in navigator && 
                              'PushManager' in window;
        
        if (hasPushSupport) {
          // Check current permission state
          const permission = Notification.permission;
          
          if (permission === 'granted') {
            // Already granted - verify subscription exists
            const existing = await getExistingSubscription();
            if (existing) {
              localStorage.setItem(PUSH_ENABLED_KEY, 'true');
            } else {
              // Permission granted but no subscription - need to subscribe
              setShowPushPrompt(true);
            }
          } else if (permission === 'default') {
            // Not yet asked - show prompt
            setShowPushPrompt(true);
          }
          // If 'denied' - don't show (user blocked it)
        }
      }

      // === CHAT SHORTCUT ===
      // Check if already installed or dismissed
      const shortcutInstalled = localStorage.getItem(SHORTCUT_INSTALLED_KEY) === 'true';
      const shortcutDismissed = localStorage.getItem(SHORTCUT_DISMISSED_KEY) === 'true';
      
      if (!shortcutInstalled && !shortcutDismissed) {
        // Check if running in standalone mode (already installed)
        const isStandalone = window.matchMedia('(display-mode: standalone)').matches ||
                           window.navigator.standalone === true;
        
        if (isStandalone) {
          localStorage.setItem(SHORTCUT_INSTALLED_KEY, 'true');
        } else {
          // Show shortcut prompt after a delay (let push prompt show first)
          setTimeout(() => {
            if (!showPushPrompt) {
              setShowShortcutPrompt(true);
            }
          }, 500);
        }
      }
    };

    // Small delay to let app render first
    const timer = setTimeout(checkPrompts, 1000);
    return () => clearTimeout(timer);
  }, [user, showPushPrompt]);

  // Listen for install prompt
  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  // Handle push notification enable
  const handleEnablePush = async () => {
    setLoading(true);
    try {
      // Register service worker first
      if ('serviceWorker' in navigator) {
        await navigator.serviceWorker.register('/sw-chat.js');
        await navigator.serviceWorker.ready;
      }

      // Subscribe to push
      const subscription = await subscribeToPush();
      
      console.log('[AppPrompts] Subscription result:', subscription);
      
      if (subscription && subscription.endpoint && subscription.keys) {
        // Send subscription to backend - correct format!
        const userName = user.displayName || user.email?.split('@')[0] || 'User';
        
        const response = await fetch(`${BACKEND_URL}/api/push/subscribe`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            user_id: user.uid,
            user_name: userName,
            endpoint: subscription.endpoint,
            keys: subscription.keys
          })
        });

        if (response.ok) {
          console.log('[AppPrompts] Push subscription saved to backend');
          // Mark as enabled permanently
          localStorage.setItem(PUSH_ENABLED_KEY, 'true');
          setShowPushPrompt(false);
          
          // Show a test notification
          if (Notification.permission === 'granted') {
            new Notification('🔔 Push aktiviert!', {
              body: 'Du erhältst jetzt Benachrichtigungen für neue Chat-Nachrichten.',
              icon: '/icon-192.png'
            });
          }
          
          // Now show shortcut prompt if not installed
          const shortcutInstalled = localStorage.getItem(SHORTCUT_INSTALLED_KEY) === 'true';
          if (!shortcutInstalled) {
            setTimeout(() => setShowShortcutPrompt(true), 500);
          }
        } else {
          const error = await response.json().catch(() => ({}));
          console.error('[AppPrompts] Backend error:', error);
          throw new Error(error.detail || 'Backend-Fehler');
        }
      } else if (Notification.permission === 'granted') {
        // Permission granted but subscription failed
        console.warn('[AppPrompts] Permission granted but subscription failed');
        localStorage.setItem(PUSH_ENABLED_KEY, 'true');
        setShowPushPrompt(false);
        alert('✅ Browser-Berechtigung erteilt!\n\nVollständige Push-Benachrichtigungen funktionieren nach dem Deployment.');
        
        const shortcutInstalled = localStorage.getItem(SHORTCUT_INSTALLED_KEY) === 'true';
        if (!shortcutInstalled) {
          setTimeout(() => setShowShortcutPrompt(true), 500);
        }
      } else {
        throw new Error('Subscription konnte nicht erstellt werden');
      }
    } catch (error) {
      console.error('[AppPrompts] Push enable error:', error);
      alert('Push-Benachrichtigungen konnten nicht aktiviert werden:\n' + error.message);
    }
    setLoading(false);
  };

  // Handle shortcut installation
  const handleInstallShortcut = async () => {
    setLoading(true);
    
    if (deferredPrompt) {
      // Use native install prompt
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      
      if (outcome === 'accepted') {
        localStorage.setItem(SHORTCUT_INSTALLED_KEY, 'true');
      }
      setDeferredPrompt(null);
    } else {
      // Show manual instructions for iOS or browsers without install prompt
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
      
      if (isIOS) {
        alert(`📱 Chat-Shortcut auf iPhone/iPad:

1. Tippe auf das "Teilen" Symbol (Rechteck mit Pfeil)
2. Scrolle nach unten
3. Tippe "Zum Home-Bildschirm"
4. Tippe "Hinzufügen"

✅ "RS5 Chat" erscheint auf deinem Homescreen!`);
      } else {
        alert(`📱 Chat-Shortcut installieren:

In Chrome/Edge:
1. Öffne das Browser-Menü (⋮)
2. Tippe "App installieren" oder "Zum Startbildschirm hinzufügen"

✅ Die App erscheint auf deinem Homescreen!`);
      }
      
      // Mark as shown (user saw instructions)
      localStorage.setItem(SHORTCUT_INSTALLED_KEY, 'true');
    }
    
    setShowShortcutPrompt(false);
    setLoading(false);
  };

  // Dismiss push prompt (snooze für 3 Tage, damit es nicht ständig aufpoppt)
  const dismissPush = () => {
    setShowPushPrompt(false);
    localStorage.setItem(PUSH_DISMISSED_KEY, String(Date.now()));
    // Show shortcut prompt after dismissing push
    const shortcutInstalled = localStorage.getItem(SHORTCUT_INSTALLED_KEY) === 'true';
    if (!shortcutInstalled) {
      setTimeout(() => setShowShortcutPrompt(true), 300);
    }
  };

  // Dismiss shortcut prompt permanently
  const dismissShortcut = () => {
    setShowShortcutPrompt(false);
    localStorage.setItem(SHORTCUT_INSTALLED_KEY, 'true'); // Don't ask again
  };

  // Don't render if no prompts to show
  if (!showPushPrompt && !showShortcutPrompt) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
      {/* Push Notification Prompt */}
      {showPushPrompt && (
        <div className="bg-gradient-to-b from-[#1a0d11] to-[#12090c] border border-rose-500/30 rounded-2xl p-6 max-w-sm w-full shadow-2xl">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-rose-500/20 rounded-full flex items-center justify-center">
              <Bell className="text-rose-400" size={24} />
            </div>
            <button
              onClick={dismissPush}
              className="p-2 hover:bg-white/10 rounded-full transition-colors"
            >
              <X size={20} className="text-rose-200/60" />
            </button>
          </div>
          
          <h3 className="text-lg font-bold text-rose-100 mb-2">
            🔔 Push-Benachrichtigungen
          </h3>
          <p className="text-rose-200/80 text-sm mb-6">
            Erhalte sofort eine Benachrichtigung wenn jemand im Team-Chat schreibt - auch wenn die App geschlossen ist!
          </p>
          
          <div className="flex gap-3">
            <Button
              onClick={dismissPush}
              variant="outline"
              className="flex-1 border-white/20 text-rose-200 hover:bg-white/10"
            >
              Später
            </Button>
            <Button
              onClick={handleEnablePush}
              disabled={loading}
              className="flex-1 bg-rose-600 hover:bg-rose-700 text-white"
            >
              {loading ? 'Wird aktiviert...' : 'Aktivieren'}
            </Button>
          </div>
        </div>
      )}

      {/* Shortcut Install Prompt */}
      {showShortcutPrompt && !showPushPrompt && (
        <div className="bg-gradient-to-b from-[#1a0d11] to-[#12090c] border border-pink-500/30 rounded-2xl p-6 max-w-sm w-full shadow-2xl">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-pink-500/20 rounded-full flex items-center justify-center">
              <MessageSquare className="text-pink-400" size={24} />
            </div>
            <button
              onClick={dismissShortcut}
              className="p-2 hover:bg-white/10 rounded-full transition-colors"
            >
              <X size={20} className="text-rose-200/60" />
            </button>
          </div>
          
          <h3 className="text-lg font-bold text-rose-100 mb-2">
            📲 Chat-Shortcut installieren
          </h3>
          <p className="text-rose-200/80 text-sm mb-6">
            Füge den Team-Chat als eigene App auf deinen Homescreen hinzu - schneller Zugriff mit einem Tipp!
          </p>
          
          <div className="flex gap-3">
            <Button
              onClick={dismissShortcut}
              variant="outline"
              className="flex-1 border-white/20 text-rose-200 hover:bg-white/10"
            >
              Nicht jetzt
            </Button>
            <Button
              onClick={handleInstallShortcut}
              disabled={loading}
              className="flex-1 bg-pink-600 hover:bg-pink-700 text-white"
            >
              <Download size={16} className="mr-2" />
              {loading ? 'Installiere...' : 'Installieren'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
